import { useEffect } from 'react';
import { useCart } from './cart';
import { Cart } from './types';
import { NO_AFFILIATE, pushUnknownUserContext, pushUserContext, UserContext } from './analytics';

/**
 * Pushes `user_context_loaded` once the cart request settles, on pages whose server has not
 * already pushed it. Renders nothing.
 *
 * On a legacy-rendered page the server pushed the event right after the container snippet
 * (includes/tags/gtm/user_context.html), before this ever mounted, and the dispatcher sees
 * it and does nothing. On a stateless page nothing has pushed it: the context arrives with
 * the cart, which is the session and so answers for every visitor, even with nothing in it.
 *
 * There is no timeout. The page waits on the same request for everything it draws, so a
 * page whose cart never answers is broken whatever GTM hears. `loaded` turns true when the
 * request settles either way — CartProvider never rejects, it answers a failure with no
 * cart — and a failure pushes the event with nothing known, so queued events still flush.
 */
export function DataLayer() {
  const { cart, loaded } = useCart();

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (cart) {
      pushUserContext(toUserContext(cart));
    } else {
      pushUnknownUserContext();
    }
  }, [cart, loaded]);

  return null;
}

/**
 * The cart endpoint's fields in the push's shapes. `email_hashes` drops the email itself,
 * as the server-rendered push does.
 *
 * `ab_test_variant` is null, not absent, when the cart has no variant. A cart that came
 * back means the server read the session and found no test — the known "none" that clears
 * the GA4 user property, which is what the server-rendered push sends. The field is missing
 * from the response only because the serializer drops nulls, so absent here is that null.
 */
function toUserContext(cart: Cart): UserContext {
  const emailData = cart.session_email_data;

  return {
    email: emailData?.email ?? '',
    email_hashes: emailData
      ? {
          md5: emailData.md5,
          sha1: emailData.sha1,
          sha256: emailData.sha256,
          md5UC: emailData.md5UC,
          sha1UC: emailData.sha1UC,
          sha256UC: emailData.sha256UC,
        }
      : null,
    affiliate: cart.affiliate_data ?? NO_AFFILIATE,
    visitor_state: cart.visitor_state ?? '',
    ab_test_variant: cart.ab_test_variant ?? null,
  };
}
